import { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { boolean, InferType, object, ObjectSchema, string } from 'yup';

import { BaseCompanyModel, LegalPersonReadModel, handleBackendValidationErrors } from '@client/api';
import { Button, FormSubmitButton, Input, Modal, ModalContent, ModalFooter, Spinner } from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBrregCompanySearch } from '../hooks/useBrregCompanySearch';
import { BrregCompanyData } from '../hooks/useBrregCompanySearch';
import { IoIosSearch } from 'react-icons/io';

type FormModel = Omit<BaseCompanyModel, 'invoicingAccount' | 'source'>;

const schema = object({
  name: string().required(),
  vatId: string().nullable(),
  organizationNumber: string().nullable(),
  country: object({
    threeLetterCode: string().required(),
  }).nullable(),
  businessAddress: object({
    address: string().nullable(),
    postalCode: string().nullable(),
    city: string().nullable(),
    municipality: string().nullable(),
  }).nullable(),
  organizationForm: string().nullable(),
  industryCode: string().nullable(),
  institutionalSectorCode: string().nullable(),
  numberOfEmployees: string().nullable(),
  isBankrupt: boolean().nullable(),
  isUnderLiquidation: boolean().nullable(),
  isUnderForcedDissolution: boolean().nullable(),
  parentOrganizationNumber: string().nullable(),
  registeredInVatRegistry: boolean().nullable(),
  vatRegistrationDate: string().nullable(),
  lastAnnualAccountsYear: string().nullable(),
  activity: string().nullable(),
  remarks: string().nullable(),
  brregUrl: string().nullable(),
  website: string().nullable(),
  emailAddress: string().nullable(),
  mobilePhone: string().nullable(),
  entityRegistrationDate: string().nullable(),
  isPartOfGroup: boolean().nullable(),
  languageCode: string().nullable(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormValues>;
type FormDefaultValues = DeepPartial<FormValues>;

export type CompanySearchFormData = FormValues;

interface CompanySearchFormProps {
  title: React.ReactNode;
  onSubmit: (data: FormValues) => Promise<void>;
  onClose: () => void;
  company?: LegalPersonReadModel;
  open?: boolean;
}

export function CompanySearchForm({ open = true, title, onSubmit, onClose, company }: CompanySearchFormProps) {
  const { t: tCommon } = useTranslation();
  const { t: tCompanies } = useTranslation('companies');
  const { search: searchCompany, isLoading: isSearching, error: searchError } = useBrregCompanySearch();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<BrregCompanyData | null>(null);
  const [searchResults, setSearchResults] = useState<BrregCompanyData[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [skipAutoSearch, setSkipAutoSearch] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      name: company?.name ?? selectedCompany?.navn ?? '',
      vatId: company?.vatId ?? selectedCompany?.organisasjonsnummer ?? '',
    }),
    [company?.name, company?.vatId, selectedCompany],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, setError, setValue } = methods;

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit(data).catch((error) => {
      handleBackendValidationErrors(
        {
          Name: 'name',
          VatId: 'vatId',
          OrganizationNumber: 'organizationNumber',
          InvoicingAccount: 'invoicingAccount.integration',
          InvoicingAccountIdentifier: 'invoicingAccount.identifier',
        },
        error,
        (name, error) => setError(name as FormPath, error),
      );
    });
  };

  const populateForm = useCallback(
    (companyData: BrregCompanyData) => {
      setValue('name', companyData.navn, { shouldValidate: true, shouldDirty: true });
      setValue('vatId', companyData.organisasjonsnummer, { shouldValidate: true, shouldDirty: true });
      setValue('organizationNumber', companyData.organisasjonsnummer, { shouldValidate: true, shouldDirty: true });
      setValue(
        'country',
        {
          threeLetterCode: 'NOR',
        },
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        'businessAddress',
        {
          // BRREG may return multi-line addresses (e.g. ["c/o INC Skøyen", "Karenslyst allé 10"]).
          // Join all lines so the street isn't lost behind a c/o prefix. Matches onboarding import.
          address: companyData.forretningsadresse?.adresse?.length
            ? companyData.forretningsadresse.adresse.filter(Boolean).join(', ')
            : null,
          postalCode: companyData.forretningsadresse?.postnummer,
          city: companyData.forretningsadresse?.poststed,
          municipality: companyData.forretningsadresse?.kommune,
        },
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        'organizationForm',
        companyData.organisasjonsform
          ? `${companyData.organisasjonsform.kode} - ${companyData.organisasjonsform.beskrivelse}`
          : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        'industryCode',
        companyData.naeringskode1
          ? `${companyData.naeringskode1.kode} - ${companyData.naeringskode1.beskrivelse}`
          : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        'institutionalSectorCode',
        companyData.institusjonellSektorkode
          ? `${companyData.institusjonellSektorkode.kode} - ${companyData.institusjonellSektorkode.beskrivelse}`
          : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        'numberOfEmployees',
        companyData.antallAnsatte != null ? String(companyData.antallAnsatte) : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue('isBankrupt', companyData.konkurs ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('isUnderLiquidation', companyData.underAvvikling ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('isUnderForcedDissolution', companyData.underTvangsavviklingEllerTvangsopplosning ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('parentOrganizationNumber', companyData.overordnetEnhet ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('registeredInVatRegistry', companyData.registrertIMvaregisteret ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('vatRegistrationDate', companyData.registreringsdatoMerverdiavgiftsregisteret ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('lastAnnualAccountsYear', companyData.sisteInnsendteAarsregnskap ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(
        'activity',
        companyData.aktivitet && companyData.aktivitet.length > 0
          ? companyData.aktivitet.join(' ')
          : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(
        'remarks',
        companyData.paategninger && companyData.paategninger.length > 0
          ? companyData.paategninger
              .map((p) =>
                !p.dato ? `${p.type}: ${p.beskrivelse}` : `${p.type} (${p.dato}): ${p.beskrivelse}`,
              )
              .join('; ')
          : null,
        { shouldValidate: true, shouldDirty: true },
      );
      setValue('brregUrl', companyData._links?.self?.href ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('website', companyData.hjemmeside ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('emailAddress', companyData.epostadresse ?? null, { shouldValidate: true, shouldDirty: true });
      // Prefer mobile; fall back to landline so AS entities without mobile still get a phone number.
      setValue('mobilePhone', companyData.mobil ?? companyData.telefon ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('entityRegistrationDate', companyData.registreringsdatoEnhetsregisteret ?? null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('isPartOfGroup', companyData.erIKonsern ?? null, { shouldValidate: true, shouldDirty: true });
      setValue('languageCode', companyData.maalform ?? null, { shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  const handleSelectCompany = useCallback(
    (company: BrregCompanyData) => {
      setSkipAutoSearch(true);
      setSelectedCompany(company);
      setSearchQuery(company.navn);
      setShowDropdown(false);
      setSearchResults([]);
      populateForm(company);
    },
    [populateForm],
  );

  const performSearch = useCallback(
    async (query: string) => {
      if (!query || !query.trim()) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      const result = await searchCompany(query);
      if (!result) {
        if (searchError) {
          setSearchResults([]);
          setShowDropdown(true);
        }
        return;
      }

      setSearchResults(result);
      setShowDropdown(true);
      setHighlightedIndex(-1);

      // Focus input after search completes to enable keyboard navigation
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    },
    [searchCompany, searchError, populateForm, handleSelectCompany],
  );

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear selected company when user modifies the input
    if (selectedCompany && value !== selectedCompany.navn) {
      setSelectedCompany(null);
      setSkipAutoSearch(false);
    }

    if (!value.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      setSelectedCompany(null);
      return;
    }
  };

  // Auto-search after 3 characters with debounce
  useEffect(() => {
    if (skipAutoSearch) {
      setSkipAutoSearch(false);
      return;
    }

    if (searchQuery.trim().length < 3) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, performSearch, skipAutoSearch]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showDropdown && searchResults.length > 0) {
        const newIndex = highlightedIndex < searchResults.length - 1 ? highlightedIndex + 1 : highlightedIndex;
        setHighlightedIndex(newIndex);
        // Scroll item into view
        const item = dropdownRef.current?.children[newIndex] as HTMLElement;
        item?.scrollIntoView({ block: 'nearest' });
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showDropdown && searchResults.length > 0) {
        const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : 0;
        setHighlightedIndex(newIndex);
        // Scroll item into view
        const item = dropdownRef.current?.children[newIndex] as HTMLElement;
        item?.scrollIntoView({ block: 'nearest' });
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && highlightedIndex >= 0 && searchResults[highlightedIndex]) {
        handleSelectCompany(searchResults[highlightedIndex]);
      } else {
        performSearch(searchQuery);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  // Focus search input when modal opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  return (
    <Modal open={open} appear size="lg" onClose={onClose} title={title} className="overflow-visible">
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalContent className="space-y-8 px-4 pt-4">
            <article>
              <div className="mb-6 relative">
                <label className="block mb-2">{tCompanies('searchModal.title')}</label>
                <div className="relative">
                  <Input
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => {
                      if (searchResults.length > 0 && !selectedCompany) {
                        setShowDropdown(true);
                      }
                    }}
                    className="w-full"
                    disabled={isSearching}
                  />

                  {
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {isSearching ? (
                        <Spinner size="sm" className="color-brand-primary" />
                      ) : (
                        <IoIosSearch
                          className="text-secondary cursor-pointer"
                          size="20"
                          onClick={() => performSearch(searchQuery)}
                        />
                      )}
                    </div>
                  }

                  {showDropdown && !selectedCompany && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-50 w-full mt-1 bg-[#201E1A] rounded shadow-lg max-h-72 overflow-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#322F2C] [&::-webkit-scrollbar-thumb]:rounded"
                    >
                      {searchResults.length > 0 ? (
                        searchResults.map((company, index) => (
                          <button
                            key={company.organisasjonsnummer}
                            type="button"
                            onClick={() => {
                              handleSelectCompany(company);
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            className={`w-full text-left px-4 py-3 text-sm text-white transition-colors ${
                              index < searchResults.length - 1 ? 'border-b border-[#322F2C]' : ''
                            } ${highlightedIndex === index ? 'bg-[#272521]' : 'hover:bg-[#272521]'}`}
                          >
                            <div className="text-white">{company.navn}</div>
                            <div className="text-sm text-white/60">
                              {tCompanies('searchModal.orgNr', { orgNumber: company.organisasjonsnummer })}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="w-full text-left px-4 py-3 text-sm text-white">
                          {tCompanies('searchModal.noResults')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </article>
          </ModalContent>

          <ModalFooter>
            <Button color="primary-dark" onClick={onClose}>
              {tCommon('back')}
            </Button>
            <FormSubmitButton type="submit">{tCommon('save')}</FormSubmitButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
}
